"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";

import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import EmbedCode from "@/components/global/utils/EmbedCode";

import { PageVslShortSchema } from "@/sanity/pages/PageVslShortTemplate";

import keynoteArrow from "@/public/vsl-short/keynote-arrow.svg";
import candlesRight from "@/public/vsl-short/candles-right.svg";
import candlesLeft from "@/public/vsl-short/candles-left.svg";
import dots from "@/public/vsl-short/dots.svg";
import dotsM from "@/public/vsl-short/dots-m.svg";
// how much of the video loops as a muted preview before the user clicks play
const PREVIEW_LOOP_SECONDS = 10;

export default function HeroSection({
  data,
  className,
}: {
  data: PageVslShortSchema;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [started, setStarted] = useState(false);

  // Stable reference so a Sanity live-refresh (new object, same playbackId)
  // doesn't re-attach the HLS stream and reset the playhead to 0:00.
  const heroVideoSource = useMemo(
    () => data?.heroVideo,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.heroVideo?.muxVideo?.playbackId, data?.heroVideo?.embedUrl],
  );

  const hasVideo =
    heroVideoSource?.muxVideo?.playbackId || heroVideoSource?.embedUrl;

  // the embed brings its own player UI, so it replaces the whole video block
  const useEmbed = Boolean(data?.heroEmbed && data?.heroEmbedCode);

  useEffect(() => {
    // keep the inline preview muted so autoplay is allowed
    if (videoRef.current) videoRef.current.muted = true;
  }, []);

  // before the user clicks play, loop only the first 5 seconds as a teaser
  useEffect(() => {
    const video = videoRef.current;
    if (!video || started) return;

    const loopPreview = () => {
      if (video.currentTime >= PREVIEW_LOOP_SECONDS) video.currentTime = 0;
    };
    video.addEventListener("timeupdate", loopPreview);
    return () => video.removeEventListener("timeupdate", loopPreview);
  }, [started]);

  // play from the start with audio + native controls when the user clicks
  const handleStart = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.muted = false;
    video.play();
    setIsMuted(false);
    setStarted(true);
  };

  return (
    <section
      className={cn(
        "relative flex flex-col items-center px-[2rem] pt-[6rem] pb-[9rem] sm:px-[3rem] md:px-[5rem] lg:pt-[9rem] lg:pb-[14rem]",
        className,
      )}
    >
      {/* horizontal divider under the header */}
      <DecorationLine className="top-0" orientation="horizontal" style="dark" />

      {/* vertical side lines flanking the content (near edges on mobile, matching Figma inset on desktop) */}
      <DecorationLine
        className="top-0 left-[1rem] lg:left-[10.4%]"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[10.4%]"
        orientation="vertical"
        style="dark"
      />

      <Image
        src={dots}
        alt=""
        className="hidden lg:block absolute top-0 left-1/2 -translate-x-1/2 !w-full lg:!w-[119.875rem] z-0 pointer-events-none"
      />
      <Image
        src={dotsM}
        alt=""
        className="lg:hidden absolute top-0 left-1/2 -translate-x-1/2 !w-full lg:!w-[119.875rem] z-0 pointer-events-none"
      />
      <Image
        src={candlesLeft}
        alt=""
        className="hidden lg:block absolute bottom-[-10rem] left-0 lg:left-[10.4%] lg:!w-[81.875rem] z-0 pointer-events-none"
      />
      <Image
        src={candlesRight}
        alt=""
        className="hidden lg:block absolute bottom-[18.5rem] right-0 lg:right-[10.4%] lg:!w-[81.875rem] z-0 pointer-events-none"
      />

      {/* eyebrow chip */}
      {data?.heroChip?.text && (
        <Chip
          className="mb-[3rem] [&_p]:uppercase relative z-[1]"
          image={data?.heroChip?.icon}
          text={data?.heroChip?.text}
        />
      )}

      {/* headline */}
      {data?.heroTitle && (
        <div className="pixel-highlight richtext richtext-center w-[min(113.75rem,100%)] lg:w-[min(140rem,100%)] prose-em:text-[4rem] prose-em:lg:text-[8.5rem] prose-em:font-ivy prose-em:!px-0 lg:prose-em:!px-[.7rem] prose-headings:font-funnel prose-headings:font-medium prose-headings:text-black prose-headings:text-[4.5rem] sm:prose-headings:text-[6.5rem] lg:prose-headings:text-[8.5rem] prose-headings:leading-[1.2] prose-headings:text-balance">
          <PortableText value={data.heroTitle} components={richTextComponent} />
        </div>
      )}

      {/* subtext */}
      {data?.heroText && (
        <div className="richtext richtext-center mt-[2rem] w-[min(63rem,100%)] prose-p:text-black/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.5]">
          <PortableText value={data.heroText} components={richTextComponent} />
        </div>
      )}

      {/* embed snippet — sizes itself, so no forced aspect ratio here */}
      {useEmbed ? (
        <div className="relative mt-[6rem] w-[min(133rem,100%)] overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
          <EmbedCode code={data.heroEmbedCode} />
        </div>
      ) : /* video — autoplays muted inline; click restarts from 0 with audio */
      hasVideo ? (
        <div className="relative mt-[6rem] w-[min(133rem,100%)] aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
          <HLSVideoPlayer
            ref={videoRef}
            source={heroVideoSource}
            autoPlay={!started}
            muted={isMuted}
            loop={!started}
            playsInline
            controls={started}
            poster={data?.heroVideoPlaceholder?.src}
            className="absolute left-0 top-0 !h-full !w-full object-cover"
          />

          {!started && (
            <button
              onClick={handleStart}
              className="absolute top-1/2 left-1/2 rounded-full border -translate-x-1/2 -translate-y-1/2 hover:cursor-pointer border-white/20 p-[0.5rem]"
              aria-label={data?.heroPlayLabel || "Jetzt Keynote starten"}
            >
              <span className="flex justify-center items-center rounded-full h-[7rem] gap-[1rem] bg-[#D9F9B9] px-[3rem] py-[1rem]">
                <svg
                  className="h-[2rem] w-[2rem]"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M4 2.5v11l9-5.5-9-5.5z" fill="rgba(0,0,0,0.8)" />
                </svg>
                <span className="font-medium leading-none whitespace-nowrap font-geist text-[2.25rem] text-black/80">
                  {data?.heroPlayLabel || "Jetzt Keynote starten"}
                </span>
              </span>
            </button>
          )}

          {/* green callout tag (bottom-right) — hidden once playing */}
          {data?.heroVideoTag && !started && (
            <div className="absolute bottom-0 right-[-1px] z-10 max-w-[65%] lg:max-w-[min(52rem,85%)] bg-[#D9F9B9] px-[1.75rem] py-[1rem] lg:px-[3rem] lg:py-[1.5rem] [clip-path:polygon(1.75rem_0,100%_0,100%_100%,0_100%,0_1.75rem)] lg:[clip-path:polygon(2.5rem_0,100%_0,100%_100%,0_100%,0_2.5rem)]">
              <div className="richtext richtext-start prose-p:mb-0 prose-p:text-[#0A1A1D] prose-p:text-[2rem] prose-p:leading-[1.1] lg:prose-p:text-[4.25rem] prose-strong:font-medium prose-strong:text-[#0A1A1D] prose-em:font-ivy">
                <PortableText
                  value={data.heroVideoTag}
                  components={richTextComponent}
                />
              </div>
            </div>
          )}

          {/* curved arrow pointing at the right side of the play button — desktop only, hidden once playing */}
          {data?.heroVideoTag && !started && (
            <Image
              src={keynoteArrow}
              alt=""
              aria-hidden
              className="pointer-events-none absolute top-[50%] right-[18%] hidden w-[24rem] lg:block"
            />
          )}
        </div>
      ) : (
        data?.heroVideoPlaceholder?.src && (
          <div className="relative mt-[6rem] w-[min(133rem,100%)] aspect-video overflow-clip [clip-path:polygon(0_0,calc(100%-4rem)_0,100%_4rem,100%_100%,0_100%)]">
            <Image
              className="absolute left-0 top-0 !h-full !w-full object-cover"
              src={data.heroVideoPlaceholder}
              alt=""
            />
          </div>
        )
      )}

      {/* cta */}
      {data?.heroCTA && (
        <CTACornerButton className="mt-[3rem]" data={data.heroCTA} />
      )}
    </section>
  );
}
